import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasManualTradingOrigin, resolvedManualTradingOrigin } from '@/config/api';
import {
  isManualTradingConfigured,
  listPositionGroupHistory,
  type ListPositionGroupHistoryParams,
  type PositionGroupHistoryListView,
  type PositionGroupHistoryView,
} from '@/services/manualTradingApi';
import '@/components/ManualTradingPanel/ManualTradingPanel.css';
import './PositionGroupHistory.css';
import { GroupExecutionDetail } from './GroupExecutionDetail';

function shortExchangeLabel(exchange: string): string {
  const map: Record<string, string> = {
    OKX: 'OKX',
    Bybit: 'BB',
    Binance: 'BN',
    Gateio: 'GT',
    Bitget: 'BG',
    Hyperliquid: 'HL',
    Lighter: 'LT',
    Aster: 'AS',
    EdgeX: 'EX',
    Paradex: 'PD',
  };
  return map[exchange] ?? exchange.slice(0, 4);
}

function formatUsd(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits })}`;
}

function formatSize(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function formatTs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function pnlClass(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v === 0) return '';
  return v > 0 ? 'is-positive' : 'is-negative';
}

function msFromDateInput(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const ms = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(ms) ? ms : undefined;
}

function LegDirection({ row }: { row: PositionGroupHistoryView }) {
  return (
    <span className="trading-engine-leg-dir">
      <span
        className="trading-engine-leg is-long"
        title={`Long ${row.long_exchange} · ${row.long_inst_id}`}
      >
        L&nbsp;{shortExchangeLabel(row.long_exchange)}
      </span>
      <span
        className="trading-engine-leg is-short"
        title={`Short ${row.short_exchange} · ${row.short_inst_id}`}
      >
        S&nbsp;{shortExchangeLabel(row.short_exchange)}
      </span>
    </span>
  );
}

type Filters = {
  symbol: string;
  createdFrom: string;
  createdTo: string;
};

const DEFAULT_FILTERS: Filters = {
  symbol: '',
  createdFrom: '',
  createdTo: '',
};

function filtersToParams(filters: Filters): ListPositionGroupHistoryParams {
  return {
    symbol: filters.symbol.trim() || undefined,
    created_from_ms: msFromDateInput(filters.createdFrom),
    created_to_ms: msFromDateInput(filters.createdTo)
      ? msFromDateInput(filters.createdTo)! + 86_399_999
      : undefined,
  };
}

export function PositionGroupHistory({ embedded = false }: { embedded?: boolean }) {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [data, setData] = useState<PositionGroupHistoryListView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async (nextFilters: Filters) => {
    if (!isManualTradingConfigured()) return;
    setLoading(true);
    try {
      const result = await listPositionGroupHistory(filtersToParams(nextFilters));
      if (!mounted.current) return;
      setData(result);
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!isManualTradingConfigured()) return undefined;
    void load(appliedFilters);
    return () => {
      mounted.current = false;
    };
  }, [appliedFilters, load]);

  const symbolOptions = useMemo(() => {
    const fromRows = data?.items.map((row) => row.symbol) ?? [];
    return Array.from(new Set(fromRows)).sort((a, b) => a.localeCompare(b));
  }, [data?.items]);

  const applyFilters = () => {
    setExpandedGroupId(null);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    setExpandedGroupId(null);
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const detailColSpan = 13;

  const toggleGroupDetail = (groupId: string) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  };

  if (!hasManualTradingOrigin()) {
    return (
      <div className={`pg-history-page${embedded ? ' is-embedded' : ''}`}>
        {!embedded ? (
          <header className="pg-history-header">
            <h2>持仓历史</h2>
            <p className="pg-history-desc">
              未配置 trading-engine API（<code>VITE_MANUAL_TRADING_ORIGIN</code>）。
            </p>
          </header>
        ) : (
          <p className="pg-history-desc">
            未配置 trading-engine API（<code>VITE_MANUAL_TRADING_ORIGIN</code>）。
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`pg-history-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded ? (
        <header className="pg-history-header">
          <h2>持仓历史</h2>
          <p className="pg-history-desc">
            按 position group 聚合关仓单腿明细（<code>user_runtime_position_leg_closes</code>），展示关仓量、盈亏与费用。点击行首 ▸ 可展开 task / order 执行明细。数据源：
            <code>{resolvedManualTradingOrigin()}</code>
          </p>
        </header>
      ) : null}

      <section className="pg-history-filters">
        <label className="pg-history-filter-field">
          Symbol
          <input
            list="pg-history-symbol-options"
            value={filters.symbol}
            placeholder="全部"
            onChange={(e) => setFilters((prev) => ({ ...prev, symbol: e.target.value }))}
          />
          <datalist id="pg-history-symbol-options">
            {symbolOptions.map((sym) => (
              <option key={sym} value={sym} />
            ))}
          </datalist>
        </label>
        <label className="pg-history-filter-field">
          记录时间起
          <input
            type="date"
            value={filters.createdFrom}
            onChange={(e) => setFilters((prev) => ({ ...prev, createdFrom: e.target.value }))}
          />
        </label>
        <label className="pg-history-filter-field">
          记录时间止
          <input
            type="date"
            value={filters.createdTo}
            onChange={(e) => setFilters((prev) => ({ ...prev, createdTo: e.target.value }))}
          />
        </label>
        <div className="pg-history-filter-actions">
          <button type="button" disabled={loading} onClick={applyFilters}>
            查询
          </button>
          <button type="button" className="is-secondary" disabled={loading} onClick={resetFilters}>
            重置
          </button>
          <button
            type="button"
            className="is-secondary"
            disabled={loading}
            onClick={() => void load(appliedFilters)}
          >
            {loading ? '刷新中…' : '刷新'}
          </button>
        </div>
      </section>

      {data?.summary ? (
        <section className="pg-history-summary">
          <div className="pg-history-summary-card">
            <span className="pg-history-summary-label">Group 数</span>
            <strong>{data.summary.count}</strong>
          </div>
          <div className="pg-history-summary-card">
            <span className="pg-history-summary-label">总盈亏</span>
            <strong className={pnlClass(data.summary.total_pnl)}>{formatUsd(data.summary.total_pnl)}</strong>
          </div>
          <div className="pg-history-summary-card">
            <span className="pg-history-summary-label">价差盈亏</span>
            <strong className={pnlClass(data.summary.spread_pnl)}>{formatUsd(data.summary.spread_pnl)}</strong>
          </div>
          <div className="pg-history-summary-card">
            <span className="pg-history-summary-label">资金费</span>
            <strong className={pnlClass(data.summary.funding_fee_net)}>
              {formatUsd(data.summary.funding_fee_net)}
            </strong>
          </div>
          <div className="pg-history-summary-card">
            <span className="pg-history-summary-label">手续费</span>
            <strong className="is-negative">{formatUsd(-Math.abs(data.summary.trading_fee))}</strong>
          </div>
        </section>
      ) : null}

      <p className="pg-history-meta">
        共 {data?.total ?? 0} 条
        {lastOkAt ? ` · 更新于 ${new Date(lastOkAt).toLocaleTimeString('zh-CN', { hour12: false })}` : ''}
      </p>

      {error ? <p className="pg-history-error">{error}</p> : null}

      <div className="pg-history-table-wrap">
        {!loading && (data?.items.length ?? 0) === 0 && !error ? (
          <p className="pg-history-empty">暂无符合条件的持仓历史（需有关仓成交并已落库）。</p>
        ) : (
          <table className="pg-history-table">
            <thead>
              <tr>
                <th className="pg-history-col-expand" aria-label="展开" />
                <th>Symbol</th>
                <th>方向</th>
                <th className="num">关仓量</th>
                <th className="num">关仓名义</th>
                <th className="num">关仓笔数</th>
                <th className="num">总盈亏</th>
                <th className="num">价差盈亏</th>
                <th className="num">资金费</th>
                <th className="num">手续费</th>
                <th>首次关仓</th>
                <th>最后关仓</th>
                <th>Group ID</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).flatMap((row) => {
                const expanded = expandedGroupId === row.group_id;
                return [
                  <tr
                    key={row.group_id}
                    className={`pg-history-row${expanded ? ' is-expanded' : ''}`}
                  >
                    <td className="pg-history-col-expand">
                      <button
                        type="button"
                        className="pg-history-expand-btn"
                        aria-expanded={expanded}
                        aria-label={expanded ? '收起执行明细' : '查看 task / order 执行明细'}
                        title={expanded ? '收起' : '查看 task / order 明细'}
                        onClick={() => toggleGroupDetail(row.group_id)}
                      >
                        {expanded ? '▾' : '▸'}
                      </button>
                    </td>
                    <td className="pg-history-symbol">{row.symbol}</td>
                    <td>
                      <LegDirection row={row} />
                    </td>
                    <td className="num" title={`quantity: ${formatSize(row.closed_quantity)}`}>
                      {formatSize(row.closed_token_size)}
                    </td>
                    <td className="num">{formatUsd(row.closed_notional_usd, 0)}</td>
                    <td className="num">{row.close_leg_count}</td>
                    <td className={`num ${pnlClass(row.total_pnl)}`}>{formatUsd(row.total_pnl)}</td>
                    <td className={`num ${pnlClass(row.spread_pnl)}`}>{formatUsd(row.spread_pnl)}</td>
                    <td className={`num ${pnlClass(row.funding_fee_net)}`}>{formatUsd(row.funding_fee_net)}</td>
                    <td className="num is-negative">{formatUsd(-Math.abs(row.trading_fee))}</td>
                    <td>{formatTs(row.close_started_at_ms)}</td>
                    <td>{formatTs(row.completed_at_ms)}</td>
                    <td className="pg-history-id" title={row.group_id}>
                      {row.group_id.length > 18
                        ? `${row.group_id.slice(0, 8)}…${row.group_id.slice(-6)}`
                        : row.group_id}
                    </td>
                  </tr>,
                  expanded ? (
                    <tr key={`${row.group_id}-detail`} className="pg-history-detail-row">
                      <td colSpan={detailColSpan}>
                        <GroupExecutionDetail group={row} />
                      </td>
                    </tr>
                  ) : null,
                ].filter(Boolean);
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
